'use client';

// INVARIANT(scriptera): СЦЕНА-ПРИМЕР НАРИСОВАНА В ДВУХ МЕСТАХ — веб (apps/web: components/auth/app-onboarding.tsx,
// components/auth/onboarding.css, components/mock-phone.{tsx,css}, app/[locale]/wizard-scene/[scene]/*,
// lib/scene-script.ts) и iOS (scriptera-app: ios/App/App/Kit/Scenes/*). Правишь вид, шаг, текст-раскладку, тайминг,
// цвет или размер — правь ОБА места тем же днём и сверяй кадры склейкой «веб | iOS» (~/ops/native-scenes). Причина:
// владелец 26.10.2026 — сцены в приложении нативные, «пиксель в пиксель». Канон: ~/projects/NATIVE-SCENES-TZ.md,
// docs/HOW-IT-WORKS.md §«Сцены-примеры».

// Интро мобильного приложения (docs/ONBOARDING-PLAN.md, НАТИВ-3, этап НВ8б): ПЯТЬ живых
// страниц перед входом. Показ — ТОЛЬКО в режиме приложения (UA ScripteraApp, isNativeApp)
// и КАЖДЫЙ раз, когда приложение открыто без входа (владелец 2026-08-26: не залогинился
// и закрыл — при следующем открытии интро снова; разлогинился — тоже снова). Никакого
// localStorage «показали один раз»: интро — прихожая неавторизованного приложения.
// «Пропустить мини-тур» закрывает его до следующего появления экрана входа. Листается
// свайпом и точками; язык переключается прямо здесь (сессии ещё нет).
//
// ЧТО ЗДЕСЬ ГЛАВНОЕ (владелец 27.08, НВ8): внутри нативной шторки показывается ЭТОТ веб
// (правится деплоем, без пересборок приложения), а натив остаётся рамкой. Поэтому страницы
// повторяют нативные сюжеты один в один: нарисованный телефон, ИГРАЮЩАЯСЯ сцена (человек
// сам жмёт пульсирующие места) и результат прямым текстом на плашке с конфетти. Экран
// внутри телефона обязан быть ПОХОЖ НА НАСТОЯЩИЙ мессенджер: сцены 1-2 — директ и
// комментарии Инстаграма, сцена 3 — переписка Телеграма. Числа и цвета имитации живут
// в onboarding.css (плита «ЧУЖИЕ ЭКРАНЫ»), наших розовых внутри этих экранов нет вовсе.
//
// Класс html.in-sheet ставит натив на время шторки: веб прячет свой хром (знак, имя,
// щёлкалки, «Войти») — «лого и всякой хуйни» в шторке нет. В этом режиме приветствия у
// веба тоже нет: выбор человек уже сделал на нативном герое (helloChoice examples).
//
// Позиционирование (CLAUDE.md): продаём результат. Слов «схема», «автоматизация»,
// «воркфлоу», «узел» в текстах нет; НАШИХ цен и оплат в приложении нет вовсе — цена на
// экране «Доводит до оплаты» принадлежит товару КЛИЕНТА и показана его покупателю.
import { type CSSProperties, type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  Camera,
  Check,
  CheckCheck,
  ChevronLeft,
  CirclePlus,
  FileText,
  Image as ImageIcon,
  Heart,
  MessageCircle,
  MoreHorizontal,
  Paperclip,
  Phone,
  RotateCcw,
  Send,
  Video,
} from 'lucide-react';
import { KBareButton } from '@scriptera/ui/kit';
import { IgGlyph, PhoneStatus, PlaneGlyph, TgGlyph } from '@/components/mock-phone';
import { SCENE_AVA, SCENE_BOT_AVA, SCENE_POST_IMG } from '@/lib/scene-assets';
import { sceneDefs, type SceneDef, type SceneStep, type SceneT } from '@/lib/scene-script';
import { expandSeries } from '@/lib/scene-series';

/** Настоящие фото-аватары (владелец 28.08): берутся с лендинга, раздаются ролям.
 *  Список путей живёт в lib/scene-assets — оттуда же их греет заранее список ботов и
 *  окно «+» (Н16), и разойтись грев с показом не может. */
const AVA = SCENE_AVA;

function Pava({ src, size }: { src: string; size: number }) {
  // Аватарка не загрузилась (протухла ссылка, нет фото, окно без входа) — вместо битого
  // значка «?» показываем нейтральный фирменный кружок (владелец 03.09.2026: «нужна
  // проверка, аватарки — только когда есть»).
  const [broken, setBroken] = useState(false);
  if (!src || broken) {
    return (
      <span
        className="onb-pava"
        style={{
          width: size,
          height: size,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        aria-hidden
      >
        <BrandBolt size={Math.round(size * 0.6)} />
      </span>
    );
  }
  // eslint-disable-next-line @next/next/no-img-element
  return (
    <img
      className="onb-pava"
      src={src}
      width={size}
      height={size}
      alt=""
      onError={() => setBroken(true)}
    />
  );
}
import { Link, usePathname, useRouter } from '@/i18n/routing';
import { BrandBolt } from '@/components/brand-bolt';
import { openAuth } from '@/components/auth/auth-events';
import { rememberLangCookie } from '@/components/cabinet/locale-live';
import { LocaleSwitch } from '@/components/locale-switch';
import { ThemeToggle, applyTheme } from '@/components/theme';
import { isNativeApp, nativeBridge, nativeMethod } from '@/lib/native-app';
import { pickNicheForVisit } from '@/lib/niche-local';
import { nichePhrases, nicheScene, type NicheView } from '@/lib/niche-scene';
import { isWizardScene } from '@/lib/wizard-scene-texts';
import { usePersistentData } from '@/lib/use-persistent-data';
import './onboarding.css';

/** Значки сервисов пятой сцены — файлы общего хранилища (канон «иконки интеграций
 *  везде»); чужие фирменные цвета не перекрашиваем. `dark` — тёмная пара значка-«чернил»
 *  из того же хранилища: без неё Битрикс24 и GetCourse пропадают на тёмной теме. */

// ── Движок сцены ───────────────────────────────────────────────────────────────────
// Ровно тот же набор шагов, что у натива (IntroPagesView, IntroStep): пять типов из ТЗ
// (.bot/.draft/.chip/.result/.card) и кирпичи конкретных сюжетов (.post/.jump/.ways/
// .services). Такт один: шаг играется, ход ЖДЁТ нажатия там, где ждёт натив.

type Step = SceneStep;
export type { SceneDef };

/** Частица конфетти: заранее посчитанный путь (вверх настрелом и обязательно вниз). */
interface Bit {
  i: number;
  x1: number;
  y1: number;
  x: number;
  y: number;
  r: number;
  d: number;
  bar: boolean;
  c: number;
}

interface Plate {
  id: number;
  text: string;
  slot: number;
  bits: Bit[];
  /** БОЛЬШОЙ ИТОГ (сцены цели, владелец 24.10.2026: «недостаточно заметны»): две строки — что случилось и
   *  крупно «+1 подписчик» / «+1 продажа», конфетти гуще и крупнее, плашка живёт дольше. */
  big?: boolean;
}

let plateSeq = 0;

function reducedMotion(): boolean {
  return (
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** Лёгкий отклик пальцу там, где натив бьёт хаптиком (НВ7 п.7). iOS вибрацию веба
 *  не умеет вовсе (владелец 27.08 «вибрации нет») — в приложении зовём родной
 *  хаптик моста, navigator.vibrate остаётся браузерам Андроида. */
function buzz() {
  if (reducedMotion()) return;
  if (isNativeApp() && nativeMethod('haptic')) {
    nativeBridge()
      ?.haptic?.({ kind: 'light' })
      ?.catch(() => {});
    return;
  }
  try {
    navigator.vibrate?.(20);
  } catch {
    /* вибрации нет — молча живём дальше */
  }
}

/** Залп: частицы цветов палитры (--accent / --accent-soft / --accent-stroke / --green),
 *  кружки и узкие полоски вперемешку. Праздник, а не мусор (НВ7 п.6). На своих сцену
 *  залп ГУЩЕ и шире — вверх и вбок (владелец 27.08: «конфетти слишком мало»). */
function confettiBits(big?: boolean, huge?: boolean): Bit[] {
  const out: Bit[] = [];
  const n = huge ? 120 : big ? 64 : 34;
  const side = huge ? 230 : big ? 170 : 110;
  for (let i = 0; i < n; i += 1) {
    const dx = (Math.random() * 2 - 1) * side;
    out.push({
      i,
      x1: Math.round(dx * 0.45),
      y1: -Math.round((huge ? 90 : big ? 60 : 40) + Math.random() * (huge ? 130 : big ? 95 : 60)),
      x: Math.round(dx * (big || huge ? 1.15 : 1)),
      y: Math.round((huge ? 150 : 110) + Math.random() * (huge ? 110 : 70)),
      r: Math.round((Math.random() * 2 - 1) * 540),
      d: Math.round(Math.random() * 180) / 1000,
      bar: i % 2 === 0,
      c: i % 4,
    });
  }
  return out;
}

// Сцену и набор сюжетов делит секретный лендинг (блок «Так это выглядит у клиента»):
// содержимое одно — из приложения, оболочка своя (приказ владельца 27.08).
export function Scene({
  def: defIn,
  on,
  active,
  onNext,
  isLast,
  still,
  replay: showReplay,
}: {
  def: SceneDef;
  /** Сцена играет (движок жив). Все страницы шторки играют с монтирования — свайп
   *  показывает готовое состояние мгновенно (владелец 28.08 «жду полторы секунды»). */
  on: boolean;
  /** Страница сейчас на экране: только она вибрирует и бьёт хаптиком. */
  active?: boolean;
  onNext?: () => void;
  /** Последний слайд: вместо «Ещё пример» — «Зарегистрироваться» (владелец 28.08).
   *  Что считать последним, решает ВЕБ — слайды и кнопки правятся деплоем веба. */
  isLast?: boolean;
  /** СТАТИЧНЫЙ РЕЖИМ (ТЗ-3 Т1): своих кнопок «Дальше»/«Заново» сцена не показывает —
   *  она стоит внутри чужого экрана (первый шаг мастера анкеты), и там «Дальше» своя.
   *  Больше режим не меняет ничего: сцена та же самая, лендинг и мини-тур не трогаем. */
  still?: boolean;
  /** ПОВТОР В СТАТИЧНОМ РЕЖИМЕ (ТЗ-9 Е1, владелец 2026-08-30: «верни кнопку показать
   *  повторно»): в чужом экране «Заново» нужна, а «Ещё пример» — нет (там своя кнопка
   *  внизу). Проп только ОТКРЫВАЕТ «Заново» при still; без still и без него поведение
   *  тура и лендинга прежнее до пикселя. */
  replay?: boolean;
}) {
  // §8.22: пример цепочки — пять случайных сообщений набора на этот показ (выборка один раз за жизнь показа).
  const def = useMemo(() => expandSeries(defIn), [defIn]);
  const t = useTranslations('auth.onboarding');
  /** Какой шаг сейчас играется (−1 — сцена ещё не начиналась). Внутри чужого экрана
   *  (still — первый шаг анкеты) начинаем с ПЕРВОГО ЖЕ РЕНДЕРА: телефон не должен
   *  простоять пустым ни одного кадра (Н16, владелец 02.09.2026). */
  const [cur, setCur] = useState(still && on ? 0 : -1);
  /** Сколько шагов уже показано в ленте. */
  const [vis, setVis] = useState(0);
  const [typing, setTyping] = useState(false);
  const [draft, setDraft] = useState('');
  /** Чего ждём от человека: отправки черновика или нажатия чипа/карточки. */
  const [wait, setWait] = useState<'send' | 'tap' | null>(null);
  /** Тряска-подсказка: 6 секунд не нажимают — живое место качается и вибрирует. */
  const [wig, setWig] = useState(false);
  /** Какой формат курса выбран кнопкой меню (третья сцена). */
  const [chosen, setChosen] = useState(0);
  /* Вспышка нажатия — ТОЛЬКО у нажатой кнопки (владелец 28.08: «эффект нажатия сразу
     для всех трёх»): раньше все кнопки разом рвали свечение ожидания. */
  const [hit, setHit] = useState<string | null>(null);
  const [plates, setPlates] = useState<Plate[]>([]);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const draftTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeRef = useRef(active !== false);
  activeRef.current = active !== false;
  /** Вибрация/хаптик — только у видимой страницы: пять параллельных сцен иначе жужжат хором. */
  const tick = () => {
    if (activeRef.current) buzz();
  };

  const later = (ms: number, fn: () => void) => {
    timers.current.push(setTimeout(fn, ms));
  };

  useEffect(
    () => () => {
      for (const id of timers.current) clearTimeout(id);
      timers.current = [];
    },
    [],
  );

  // Сцена начинается через мгновение после ПРИХОДА на страницу и играется с начала;
  // в ленте-треке смонтированы все пять, но живёт только открытая (как у натива).
  useEffect(() => {
    if (!on) {
      for (const id of timers.current) clearTimeout(id);
      timers.current = [];
      setCur(-1);
      setVis(0);
      setTyping(false);
      setDraft('');
      setWait(null);
      setWig(false);
      setPlates([]);
      return undefined;
    }
    // ВНУТРИ ЧУЖОГО ЭКРАНА СЦЕНА НАЧИНАЕТСЯ СРАЗУ (владелец 02.09.2026, Н16: «мокап
    // примера стоит пустым ~0,7 с… потом появляется картинка»). Вступительная пауза в
    // 400 мс придумана для тура и лендинга, где сцена — самостоятельный экран и вдох
    // перед началом уместен; на первом шаге анкеты (still) это чистое пустое место:
    // человек только что нажал шаблон и смотрит на пустой телефон. Тур и лендинг
    // (без still) идут как шли — пауза там осталась ровно та же.
    if (still) {
      setCur(0);
      return undefined;
    }
    const id = setTimeout(() => setCur(0), 400);
    return () => clearTimeout(id);
  }, [on, still]);

  const showPlate = (text: string, big = false) => {
    plateSeq += 1;
    const id = plateSeq;
    const bits = reducedMotion() ? [] : confettiBits(def.own, big);
    setPlates((p) => [...p, { id, text, slot: p.length, bits, big }]);
    // Плашка живёт долго на ВСЕХ сценах (владелец 28.08: дёрганье убрать везде,
    // не по одному) — снимаем, когда уход догорел.
    later(big ? 4350 : 2950, () => setPlates((p) => p.filter((x) => x.id !== id)));
  };

  // Такт: один шаг — один заход. Ходы, которые ждут человека (черновик, чип, карточки
  // путей), такт не передают — его двигают обработчики нажатий.
  useEffect(() => {
    if (cur < 0) return undefined;
    const step = def.steps[cur];
    if (!step) return undefined;
    const stops: (() => void)[] = [];
    const go = (ms: number) => {
      const id = setTimeout(() => setCur(cur + 1), ms);
      stops.push(() => clearTimeout(id));
    };
    switch (step.k) {
      case 'post':
        setVis(cur + 1);
        go(600);
        break;
      case 'jump':
      case 'day':
        setVis(cur + 1);
        go(550);
        break;
      case 'file':
      case 'product':
      case 'echo':
        setVis(cur + 1);
        go(600);
        break;
      case 'bot': {
        setTyping(true);
        const id = setTimeout(() => {
          setTyping(false);
          setVis(cur + 1);
        }, 900);
        stops.push(() => clearTimeout(id));
        go(1450);
        break;
      }
      case 'result':
        setVis(cur + 1);
        showPlate(step.text, step.big === true);
        go(step.big ? 1400 : 750);
        break;
      case 'chip':
      case 'note':
      case 'start':
      case 'menu':
        setVis(cur + 1);
        // QUICK-START §11.20 п.2: кнопка сообщения серии (`auto`) стоит под пузырём, но сцена её не ждёт — дальше по
        // таймингу бота; нажатия просит только последняя («Купить»).
        if (step.k === 'chip' && step.auto) {
          go(900);
          break;
        }
        setWait('tap');
        tick();
        break;
      case 'bill':
      case 'echoPick':
        setVis(cur + 1);
        go(650);
        break;
      case 'draft': {
        // Первое сообщение ПЕЧАТАЕТСЯ само, дальше сцена ждёт настоящего нажатия
        // (палец-демонстратор владелец отменил, НВ7 п.9).
        const chars = [...step.text];
        let n = 0;
        const id = setInterval(() => {
          /* печать; ссылку держит draftTimer — досрочный тап по самолёту её прибьёт */
          n += 1;
          setDraft(chars.slice(0, n).join(''));
          if (n >= chars.length) {
            clearInterval(id);
            const done = setTimeout(() => {
              setWait('send');
              tick();
            }, 150);
            stops.push(() => clearTimeout(done));
          }
        }, 50);
        draftTimer.current = id;
        stops.push(() => clearInterval(id));
        break;
      }
      default:
        break;
    }
    return () => {
      for (const stop of stops) stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur]);

  // Подсказка «нажми»: дёрг с вибрацией СРАЗУ при появлении ожидания (владелец 27.08 —
  // строка-подсказка дёргается, телефон вибрирует) и дальше каждые 6 секунд тишины.
  useEffect(() => {
    if (!wait) return undefined;
    setWig(true);
    let off: ReturnType<typeof setTimeout> | null = setTimeout(() => setWig(false), 620);
    const id = setInterval(() => {
      setWig(true);
      tick();
      off = setTimeout(() => setWig(false), 620);
    }, 6000);
    return () => {
      clearInterval(id);
      if (off) clearTimeout(off);
    };
  }, [wait]);

  /** «Заново»: сцена сбрасывается и играется с начала (владелец 28.08). */
  const replay = () => {
    for (const id of timers.current) clearTimeout(id);
    timers.current = [];
    if (draftTimer.current) {
      clearInterval(draftTimer.current);
      draftTimer.current = null;
    }
    setCur(-1);
    setVis(0);
    setTyping(false);
    setDraft('');
    setWait(null);
    setWig(false);
    setChosen(0);
    setHit(null);
    setPlates([]);
    timers.current.push(setTimeout(() => setCur(0), 400));
  };

  const tapSend = () => {
    // Самолёт живой и ВО ВРЕМЯ печати (владелец 28.08): тап отправляет весь текст
    // сразу, не дожидаясь конца набора.
    if (wait !== 'send' && def.steps[cur]?.k !== 'draft') return;
    if (draftTimer.current) {
      clearInterval(draftTimer.current);
      draftTimer.current = null;
    }
    setWait(null);
    setWig(false);
    setDraft('');
    setVis(cur + 1);
    later(500, () => setCur(cur + 1));
  };

  const tapChip = () => {
    if (wait !== 'tap') return;
    setWait(null);
    setWig(false);
    later(350, () => setCur(cur + 1));
  };

  const jumpAt = def.steps.findIndex((s) => s.k === 'jump');
  /** До перехода в директ сюжет второй сцены идёт комментариями под постом. */
  const inComments = (i: number) => !!def.comments && (jumpAt < 0 || i < jumpAt);
  /** Сцена комментариев перешла в личку (после «Нового сообщения»). */
  const inDm = !!def.comments && jumpAt >= 0 && vis > jumpAt;
  /** ПЕРЕХОД В ТЕЛЕГРАМ ВНУТРИ ОДНОЙ СЦЕНЫ (владелец 22.09.2026, сцена s6 «продвинутый
   *  бот»): шаг `jump` с `to: 'tg'` переключает нарисованный телефон на переписку
   *  Телеграма — шапка, поле ввода и пузыри берутся у телеграмных сцен как есть, а
   *  история Инстаграма в ленте прячется. До этого шага сцена живёт по `def.chat`. */
  const tgAt = def.steps.findIndex((s) => s.k === 'jump' && s.to === 'tg');
  const inTg = tgAt >= 0 && vis > tgAt;
  const chat: 'ig' | 'tg' = inTg ? 'tg' : def.chat;

  /** Часы переписки: от ТЕКУЩЕГО времени пользователя на момент открытия сцены
   *  (владелец 28.08 — «показывай текущее время»), дальше по минуте на ход. База
   *  ставится на клиенте после маунта — гидрация не рвётся. */
  const baseMin = useRef<number | null>(null);
  useEffect(() => {
    const d = new Date();
    baseMin.current = d.getHours() * 60 + d.getMinutes();
  }, []);
  const stamp = (i: number) => {
    let n = 0;
    for (let j = 0; j < i; j += 1) {
      const k = def.steps[j]?.k;
      if (k === 'bot' || k === 'draft' || k === 'file' || k === 'product' || k === 'bill') n += 1;
    }
    const total = (baseMin.current ?? 0) + n;
    const hh = String(Math.floor(total / 60) % 24);
    const mm = String(total % 60).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  /** Пузырь чужого мессенджера: у Инстаграма без времени и галочек, у Телеграма — время
   *  мелким справа-снизу ВНУТРИ пузыря, у исходящих две галочки. */
  const bubble = (body: ReactNode, mine: boolean, i: number) =>
    chat === 'ig' ? (
      <span className={`onb-b ig ${mine ? 'me' : 'them'}`}>{body}</span>
    ) : (
      <span className={`onb-b tg ${mine ? 'me' : 'them'}`}>
        {body}
        <span className="onb-tm">
          {stamp(i)}
          {mine ? <CheckCheck size={13} strokeWidth={2.2} /> : null}
        </span>
      </span>
    );

  const chipClass = def.phone ? `onb-chip ${chat}` : 'onb-chip own';

  const menuItems =
    (
      def.steps.find((x) => x.k === 'menu') as
        { items: { name: string; price: string }[] } | undefined
    )?.items ?? [];
  const picked = menuItems[chosen];

  /** Пузырь Телеграма c инлайн-кнопками ОДНИМ блоком (владелец 28.08): к пузырю
   *  шага i прикрепляются кнопки следующего шага — меню форматов или «Оплатить». */
  const tgBubble = (body: ReactNode, i: number) => {
    const next = def.steps[i + 1];
    const menu = next?.k === 'menu' && vis > i ? next.items : null;
    const pay = next?.k === 'chip' && vis > i ? next : null;
    const live = wait === 'tap' && cur === i + 1;
    // Отжившая кнопка МЕРТВА (владелец 28.08: «могу снова нажать — посчитаешь за
    // оплату»): жмётся только кнопка своего шага и только пока сценарий ждёт её.
    const hot = (j?: number) => {
      if (wait !== 'tap' || cur !== i + 1) return;
      if (j !== undefined) setChosen(j);
      setHit(`${i}:${j ?? 'pay'}`);
      tapChip();
    };
    const keyCls = (id: string) =>
      `onb-tgk${live ? ' live' : ''}${live && wig ? ' wig' : ''}${hit === id ? ' hit' : ''}`;
    return (
      <span className="onb-tgwrap">
        <span className="onb-b tg them">
          {body}
          <span className="onb-tm">{stamp(i)}</span>
        </span>
        {menu ? (
          <span className="onb-tgkeys">
            {menu.map((m, j) => (
              <KBareButton key={m.name} className={keyCls(`${i}:${j}`)} onClick={() => hot(j)}>
                {m.name} · {m.price}
              </KBareButton>
            ))}
          </span>
        ) : null}
        {pay ? (
          <span className="onb-tgkeys">
            {/* §10.12 п.4: кнопка-ссылка над кнопкой шага («Канал» над «Готово») — видна, но сцену не двигает. */}
            {pay.k === 'chip' && pay.above ? <span className="onb-tgk">{pay.above}</span> : null}
            <KBareButton className={keyCls(`${i}:pay`)} onClick={() => hot()}>
              {pay.text}
            </KBareButton>
          </span>
        ) : null}
      </span>
    );
  };

  /** Плавный сдвиг ленты (владелец 27.08 «скролл дёрганый»): при появлении строки
   *  старые НЕ прыгают вверх, а доезжают — FLIP: меряем прежние места, ставим
   *  обратный сдвиг и отпускаем переходом. Новые строки играют свой въезд. */
  const feedRef = useRef<HTMLDivElement | null>(null);
  const prevTops = useRef(new Map<Element, number>());
  const prevBottom = useRef<number | null>(null);
  const slides = useRef(new WeakMap<Element, Animation>());
  useLayoutEffect(() => {
    const el = feedRef.current;
    if (!el) return;
    const m = prevTops.current;
    const firstRun = m.size === 0;
    // ПРОКРУЧИВАЕМАЯ ЛЕНТА ДЕРЖИТСЯ У НИЗА (владелец 24.10.2026, видео 07:20 из тура приложения, 60 к/с: при каждой
    // новой строке содержимое прыгало на 6–20 px и качалось, новые строки уходили под край). На странице сцены
    // для островка лента прокручиваемая (wizard-scene.css: `.wsc .onb-feed` overflow auto, строки от верха,
    // первая — с margin-top auto); при каждом изменении ленты её прокрутка ставится на низ ДО отрисовки (этот
    // эффект — до кадра), а места строк для доезда меряются в ЭКРАННЫХ координатах (offsetTop − прокрутка) —
    // доезд остаётся ровно «на высоту новой строки», без прыжков в момент переполнения. В туре и на лендинге
    // лента без прокрутки (overflow hidden, flex-end): прокрутка там всегда 0, поведение прежнее до пикселя.
    // Лента «от верха» (комментарии под постом — `.onb-feed.cm`; страница сцены — `.wsc`): низ держим сами.
    const fromTop = getComputedStyle(el).justifyContent === 'flex-start';
    if (fromTop) {
      const over = el.scrollHeight - el.clientHeight;
      if (over > 0 && Math.abs(el.scrollTop - over) > 0.5) el.scrollTop = over;
    }
    const base = fromTop ? el.scrollTop : 0;
    if (!reducedMotion()) {
      for (const k of el.children) {
        const kk = k as HTMLElement;
        // Новая строка входит С НИЗУ ленты (как в настоящем мессенджере) тем же
        // механизмом доезда, что и сдвиг старых: прежнее место новой строки — нижний
        // край прежнего содержимого. Въездная CSS-анимация — ТОЛЬКО прозрачность
        // (владелец 28.08 «дёрганье всех сообщений»): transform в keyframes воевал с
        // инлайн-сдвигом, разрыв анимации давал скачок кадра.
        // Вход нового — только СНИЗУ ВВЕРХ: если прежний низ содержимого оказался выше
        // конечного места (в том же кадре ушли точки печати), строка не ныряет вниз,
        // а просто проявляется на месте.
        const fromBelow = firstRun ? undefined : (prevBottom.current ?? undefined);
        const prev =
          m.get(kk) ?? (fromBelow !== undefined ? Math.max(fromBelow, kk.offsetTop - base) : undefined);
        // offsetTop — ЛЕЙАУТ-позиция, transform в неё не входит: мерить rect нельзя,
        // едущая анимация давала промежуточные числа, и каждый рендер пере-запускал
        // сдвиг — строки дёргались туда-сюда (владелец 28.08 «ещё хуже»).
        const now = kk.offsetTop - base;
        if (prev !== undefined && Math.abs(prev - now) > 1) {
          // Едущий сдвиг ПРОДОЛЖАЕМ с текущего места (m42 матрицы), а не рвём к началу.
          const tf = getComputedStyle(kk).transform;
          const carry = tf && tf !== 'none' ? new DOMMatrixReadOnly(tf).m42 : 0;
          // ДОЕЗД — ОДНОЙ анимацией WAAPI, не парой запись-стиля + rAF: двухфазная
          // запись гонялась с отрисовкой, и кадр успевал показать голый сдвиг —
          // сообщение телепортировалось (владелец 28.08, скачок 47px на пузыре с
          // кнопкой). Прежний доезд гасим, новый стартует с текущего места.
          slides.current.get(kk)?.cancel();
          const a = kk.animate(
            [{ transform: `translateY(${prev - now + carry}px)` }, { transform: 'none' }],
            { duration: 400, easing: 'cubic-bezier(0.22, 0.8, 0.24, 1)' },
          );
          slides.current.set(kk, a);
        }
        m.set(kk, now);
      }
    } else {
      for (const k of el.children) m.set(k, (k as HTMLElement).offsetTop - base);
    }
    let bottom = 0;
    for (const k of el.children) {
      const kk = k as HTMLElement;
      bottom = Math.max(bottom, kk.offsetTop - base + kk.offsetHeight);
    }
    prevBottom.current = bottom;
    for (const k of [...m.keys()]) {
      if (!k.isConnected) m.delete(k);
    }
    // Только реальные изменения ленты: тряска-подсказка и такт печати лейаут не двигают.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vis, typing, plates.length]);

  /** Снимок мест — СВЕЖИЙ КАЖДЫЙ КАДР (последняя нарисованная позиция). Лейаут ленты
   *  ползёт и без React: кнопка в пузыре растёт CSS-анимацией высоты (onb-chip-grow),
   *  картинка догружается — протухший снимок заставлял доезд «компенсировать» уже
   *  показанное движение, и сообщение телепортировалось (владелец 28.08, скачок 47px). */
  useEffect(() => {
    let id = 0;
    const loop = () => {
      const el = feedRef.current;
      if (el) {
        const m = prevTops.current;
        // Экранные координаты (offsetTop − прокрутка): у ленты без прокрутки это те же offsetTop.
        const base = el.scrollTop;
        let bottom = 0;
        for (const k of el.children) {
          const kk = k as HTMLElement;
          m.set(kk, kk.offsetTop - base);
          bottom = Math.max(bottom, kk.offsetTop - base + kk.offsetHeight);
        }
        if (el.children.length) prevBottom.current = bottom;
        for (const k of [...m.keys()]) {
          if (!k.isConnected) m.delete(k);
        }
      }
      id = requestAnimationFrame(loop);
    };
    id = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(id);
  }, []);

  /** Кружок-аватар у входящих директа Инстаграма: как в настоящем — только у нижнего
   *  пузыря серии, у верхних то же место занимает невидимый двойник (ровный отступ). */
  /** Лицо владельца бота: его аватарка (портрет клиента) либо то, что у сцены своё. */
  const selfFace = def.selfAva ?? (def.ava === 'marina' ? AVA.marina : null);
  /** Публикация клиента не загрузилась (ссылка площадки живёт часы) — возвращаем свою. */
  const [postBroken, setPostBroken] = useState(false);
  const postImg = def.postImg && !postBroken ? def.postImg : SCENE_POST_IMG;
  /** Имя комментатора: из аудитории клиента, иначе — своё имя сцены. */
  const faceName = (n: number, fallback: string): string =>
    def.selfFaces?.[n]?.name?.trim() || fallback;
  /** Аватарка комментатора (О5.7): персонализировано — его настоящее фото, а нет фото
   *  (личный/приватный аккаунт) — пусто, и Pava нарисует фирменный кружок; сцена без
   *  персонализации (тур/лендинг) — прежний стоковый комментатор. */
  const faceAva = (n: number, stock: string): string => {
    const f = def.selfFaces?.[n];
    if (!f) return stock;
    return f.avatar?.trim() || '';
  };

  const ava = (i: number): ReactNode => {
    if (chat !== 'ig' || !def.phone || inComments(i)) return null;
    let ghost = false;
    let found = false;
    for (let j = i + 1; j < Math.min(vis, def.steps.length); j += 1) {
      const k = def.steps[j]?.k;
      if (k === 'chip' || k === 'result') continue;
      found = true;
      ghost = k === 'bot' || k === 'file' || k === 'product';
      break;
    }
    if (!found && typing) ghost = true;
    if (selfFace) {
      return (
        <span className={`onb-b-ava own${ghost ? ' ghost' : ''}`} aria-hidden>
          <Pava src={selfFace} size={20} />
        </span>
      );
    }
    if (def.own) {
      return (
        <span className={`onb-b-ava own${ghost ? ' ghost' : ''}`} aria-hidden>
          <BrandBolt size={20} />
        </span>
      );
    }
    return <span className={`onb-b-ava${ghost ? ' ghost' : ''}`} aria-hidden />;
  };

  const row = (step: Step, i: number): ReactNode => {
    switch (step.k) {
      case 'post':
        // БЕЗ карточки-рамки (владелец 28.08 «белые полосы»): настоящий экран
        // комментариев — пост во всю ширину, комментарии тем же потоком.
        return (
          <div className="onb-row onb-post2" key={i}>
            <span className="onb-post2-in">
              <span className="onb-post2-head">
                <Pava src={selfFace ?? AVA.marina} size={26} />
                <b>{def.nick}</b>
                <MoreHorizontal size={16} strokeWidth={2.2} aria-hidden />
              </span>
              <span className="onb-post2-img" aria-hidden>
                {/* Фото поста — прислано владельцем 28.08 (вместо рисованных схем); у
                    человека с подключённым Instagram на его месте стоит ЕГО публикация
                    (портрет клиента). Картинка не загрузилась — возвращаемся к своей. */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={postImg}
                  alt=""
                  width={1080}
                  height={580}
                  onError={() => setPostBroken(true)}
                />
              </span>
              <span className="onb-post2-act" aria-hidden>
                <Heart size={18} strokeWidth={2} />
                <MessageCircle size={18} strokeWidth={2} />
                <Send size={17} strokeWidth={2} />
              </span>
              <span className="onb-post2-cap">
                <b>{def.nick}</b> {step.text}
              </span>
              {/* Чужого комментария под постом нет (владелец 24.10.2026: убрать «Класс, забрала себе»). */}
            </span>
          </div>
        );
      case 'draft':
        return inComments(i) ? (
          <div className="onb-row onb-cmt" key={i}>
            <Pava src={faceAva(1, AVA.maks)} size={20} />
            <span className="onb-cmt-t">
              <b>{faceName(1, t('s2.c2n'))}</b> {step.text}
            </span>
          </div>
        ) : (
          <div className="onb-row me" key={i}>
            {bubble(step.text, true, i)}
          </div>
        );
      case 'bot': {
        if (inComments(i)) {
          return (
            <div className="onb-row onb-cmt re" key={i}>
              <Pava src={selfFace ?? AVA.marina} size={20} />
              <span className="onb-cmt-t">
                <b>{def.nick}</b> {step.text}
              </span>
            </div>
          );
        }
        // Кнопка бота в директе Инстаграма — белая полоса ВНУТРИ его пузыря на всю
        // ширину (живой скрин владельца 27.08), а не отдельная пилюля под ним.
        const next = def.steps[i + 1];
        // Кнопка видна СРАЗУ с пузырём (vis > i, не i+1): второй прыжок роста через
        // полтора такта дёргал пузырь (владелец 28.08); живой она станет на своём шаге.
        const chip = chat === 'ig' && next?.k === 'chip' && vis > i ? next : null;
        const live = chip !== null && wait === 'tap' && cur === i + 1;
        return (
          <div className="onb-row them" key={i}>
            {ava(i)}
            {chat === 'ig' ? (
              <span className={`onb-b ig them${chip ? ' wbtn' : ''}`}>
                {step.text}
                {chip ? (
                  <KBareButton
                    className={`onb-bchip${live ? ' live' : ''}${live && wig ? ' wig' : ''}`}
                    onClick={tapChip}
                  >
                    {chip.text}
                  </KBareButton>
                ) : null}
              </span>
            ) : (
              tgBubble(step.text, i)
            )}
          </div>
        );
      }
      case 'file':
        return (
          <div className="onb-row them" key={i}>
            {ava(i)}
            {bubble(
              <span className="onb-file">
                <span className="onb-file-i">
                  <FileText size={15} strokeWidth={2} />
                </span>
                <span>
                  <span className="onb-file-n">{step.name}</span>
                  <br />
                  <span className="onb-file-s">{step.size}</span>
                </span>
              </span>,
              false,
              i,
            )}
          </div>
        );
      case 'product':
        return (
          <div className="onb-row them" key={i}>
            {ava(i)}
            {bubble(
              <span className="onb-prod">
                <span className="onb-prod-ph" aria-hidden />
                <span>
                  <span className="onb-prod-n">{step.name}</span>
                  <br />
                  <span className="onb-prod-p">{step.price}</span>
                </span>
              </span>,
              false,
              i,
            )}
          </div>
        );
      case 'chip': {
        // На экранах телефонов кнопка уже нарисована в блоке пузыря ('bot'/'bill').
        if (def.phone) return null;
        const live = wait === 'tap' && cur === i;
        return (
          <div className="onb-row them" key={i}>
            <KBareButton
              className={`${chipClass}${live ? ' live' : ''}${live && wig ? ' wig' : ''}`}
              onClick={tapChip}
            >
              {step.text}
            </KBareButton>
          </div>
        );
      }
      case 'echo':
        return (
          <div className="onb-row me" key={i}>
            {bubble(step.text, true, i)}
          </div>
        );
      case 'start':
        // Заставка рисуется отдельной фазой (tgStart), в ленте шага нет.
        return null;
      case 'menu':
        // Кнопки живут в одном блоке с пузырём бота (случай 'bot').
        return null;
      case 'echoPick':
        return (
          <div className="onb-row me" key={i}>
            {bubble(picked ? `${picked.name} · ${picked.price}` : '', true, i)}
          </div>
        );
      case 'bill': {
        return (
          <div className="onb-row them" key={i}>
            {tgBubble(t('s3.bill', { name: picked?.name ?? '', price: picked?.price ?? '' }), i)}
          </div>
        );
      }
      case 'note': {
        const live = wait === 'tap' && cur === i;
        return (
          <div className="onb-row onb-note-row" key={i}>
            <KBareButton
              className={`onb-note-btn${live ? ' live' : ''}${live && wig ? ' wig' : ''}`}
              onClick={tapChip}
            >
              <span className="onb-note-dot" aria-hidden />
              {step.text}
            </KBareButton>
          </div>
        );
      }
      case 'day':
        // Плашка-дата Телеграма (сцена «Цепочка рассылок»): дни листаются, как в
        // настоящей переписке. Цвета — имитация ТГ («ЧУЖИЕ ЭКРАНЫ», onboarding.css).
        return (
          <div className="onb-day" key={i}>
            <span>{step.text}</span>
          </div>
        );
      case 'jump':
        // Переход в Телеграм разделителем не рисуется — сменился сам экран телефона.
        if (def.own || step.to === 'tg') return null;
        return (
          <div className="onb-sep" key={i}>
            <span>
              <ArrowDown size={12} strokeWidth={2.4} />
              {step.text}
            </span>
          </div>
        );
      default:
        // .result живёт не в ленте, а плашкой поверх сцены.
        return null;
    }
  };

  const sendLive = wait === 'send';
  const field = chat === 'tg' ? t('tgField') : def.comments && !inDm ? t('s2.field') : t('igField');
  const sendBtn = (
    <KBareButton
      className={`onb-send ${chat}${def.own ? ' own' : ''}${sendLive ? ' live' : ''}${sendLive && wig ? ' wig' : ''}`}
      aria-label={field}
      onClick={tapSend}
    >
      {(def.own && chat === 'ig') || chat === 'tg' ? <PlaneGlyph size={17} /> : <Send size={16} strokeWidth={2.2} />}
    </KBareButton>
  );

  const feed = (
    <div
      className={`onb-feed${inDm ? ' dm-in' : ''}${def.comments && !inDm && !inTg ? ' cm' : ''}`}
      key={inTg ? 'tg' : def.comments ? (inDm ? 'dm' : 'cm') : 'all'}
      ref={feedRef}
    >
      {def.own && !def.comments && def.phone && chat === 'ig' ? (
        <div className="onb-prof" aria-hidden>
          <span className="onb-prof-ava">
            {selfFace ? <Pava src={selfFace} size={64} /> : <BrandBolt size={64} />}
          </span>
          <b>{def.nick}</b>
          <span className="onb-prof-note">{t('prof.start')}</span>
        </div>
      ) : null}
      {chat === 'ig' && def.phone && !def.comments && vis > 0 ? (
        <div className="onb-meta">
          {t('igToday')} {stamp(0)}
        </div>
      ) : null}
      {def.steps
        .slice(0, vis)
        .map((s, i) => ((inDm && i <= jumpAt) || (inTg && i <= tgAt) ? null : row(s, i)))}
      {typing ? (
        <div className="onb-row them">
          {chat === 'ig' && def.phone && !inComments(Math.min(cur, def.steps.length - 1)) ? (
            selfFace ? (
              <span className="onb-b-ava own" aria-hidden>
                <Pava src={selfFace} size={20} />
              </span>
            ) : def.own ? (
              <span className="onb-b-ava own" aria-hidden>
                <BrandBolt size={20} />
              </span>
            ) : (
              <span className="onb-b-ava" aria-hidden />
            )
          ) : null}
          <span className={`onb-typ ${chat}`} aria-hidden>
            <i />
            <i />
            <i />
          </span>
        </div>
      ) : null}
    </div>
  );

  /** Телеграмная заставка бота (третья сцена): пока «Начать» не нажата. */
  // §10.5: заставка стоит и в начале (s4, s5), и после перехода в Телеграм посреди сцены (g2) — на шаге `start`.
  const startAt = def.steps.findIndex((st) => st.k === 'start');
  const atStart = startAt === 0 ? !!def.tgStart && cur <= 0 : startAt > 0 && cur === startAt;
  const startLive = atStart && wait === 'tap';

  const composer = def.phone ? (
    <div className={`onb-comp ${chat}${def.comments && !inDm ? ' cm' : ''}`}>
      <span className="onb-field">
        {chat === 'ig' && def.own ? (
          <IgGlyph className="onb-cam2" size={19} />
        ) : chat === 'ig' ? (
          <span className="onb-cam" aria-hidden>
            <Camera size={15} strokeWidth={2} />
          </span>
        ) : (
          <Paperclip className="onb-clip" size={16} strokeWidth={2} aria-hidden />
        )}
        <span className={`onb-fx${draft ? '' : ' ph'}`}>{draft || field}</span>
        {chat === 'ig' && !def.comments && !draft ? (
          <span className="onb-cico" aria-hidden>
            <ImageIcon size={15} strokeWidth={2} />
            <CirclePlus size={15} strokeWidth={2} />
          </span>
        ) : null}
        {chat === 'ig' && draft ? sendBtn : null}
        {/* Самолётик Телеграма — ВНУТРИ поля ввода, у правого края (владелец 28.08,
            скрин настоящего ТГ), не отдельным значком на обоях. */}
        {chat === 'tg' ? sendBtn : null}
      </span>
    </div>
  ) : null;

  /** Сцена доиграна: все шаги показаны — на месте подсказки встаёт яркая «Дальше»
   *  (владелец 27.08: свайпнуть догадаются не все). */
  const finished = def.steps.length > 0 && cur >= def.steps.length;

  /** Текст подсказки; на время угасания держится ПОСЛЕДНИЙ осмысленный — иначе в
   *  затухании мигает дефолтное «Нажмите кнопку» (кадры 28.08). */
  const lastHint = useRef('');
  const hintNow = wait
    ? wait === 'send'
      ? t('hint.send')
      : def.steps[cur]?.k === 'menu'
        ? t('hint.way')
        : def.steps[cur]?.k === 'start'
          ? t('hint.tapNamed', { btn: def.tgAbout?.btn ?? t('s3.startBtn') })
          : def.own && (def.steps[cur]?.k === 'chip' || def.steps[cur]?.k === 'note')
            ? t('hint.tapNamed', { btn: (def.steps[cur] as { text: string }).text })
            : t('hint.tap')
    : lastHint.current;
  if (wait) lastHint.current = hintNow;

  return (
    <div
      className={`onb-scene${def.phone ? '' : ' bare'}${def.own ? ' own' : ''}${chat === 'tg' ? ' tgm' : ''}`}
    >
      {def.phone ? (
        <div className={`onb-phone ${chat}`}>
          {/* ПОМЕТКА «ПРИМЕР ПЕРЕПИСКИ» ВМЕСТО ОСТРОВА (владелец 27.10.2026: «поставить „Пример“ прямо на изображении
              и посередине, чтобы не изменилась высота окна»; «давай да, с пилюлей сверху как на картинке»): капсулы
              острова у телефона-примера нет, на её месте — пилюля вида «Доступно в Pro». Остров остаётся только у
              предпросмотра своего бота (wizard-preview), там не пример. Пара в iOS — SceneExampleBadge (W31). */}
          <span className="onb-example">{t('exampleBadge')}</span>
          {/* Статус-бар — общий хром мокапа (mock-phone.tsx). */}
          <PhoneStatus />
          <div className={`onb-ph-top ${chat}`}>
            {chat === 'ig' && def.comments && !inDm ? (
              <>
                {/* Экран поста: трубка и камера тут неуместны (владелец 27.08) —
                    только «Комментарии», как в настоящем. */}
                <ChevronLeft className="onb-back" size={22} strokeWidth={2.4} aria-hidden />
                <span className="onb-ph-who">
                  <b>{t('s2.hdr')}</b>
                </span>
                {/* Значок Инстаграма справа фирменным градиентом (владелец 28.08). */}
                <IgGlyph className="onb-hdr-ig" size={20} brand />
              </>
            ) : chat === 'ig' ? (
              <>
                <ChevronLeft className="onb-back" size={22} strokeWidth={2.4} aria-hidden />
                {selfFace ? (
                  <span className="onb-ava own" aria-hidden>
                    <Pava src={selfFace} size={28} />
                  </span>
                ) : def.own ? (
                  <span className="onb-ava own" aria-hidden>
                    <BrandBolt size={28} />
                  </span>
                ) : (
                  <span className="onb-ring" aria-hidden>
                    <span className="onb-ava ig" />
                  </span>
                )}
                <span className="onb-ph-who">
                  <b>{def.name ?? def.nick}</b>
                  <i>{def.nick}</i>
                </span>
                <span className="onb-ph-act" aria-hidden>
                  <Phone size={19} strokeWidth={2} />
                  <Video size={21} strokeWidth={2} />
                </span>
              </>
            ) : (
              <>
                <span className="onb-ava own" aria-hidden>
                  <Pava src={def.tgAva ?? SCENE_BOT_AVA} size={28} />
                </span>
                <span className="onb-ph-who">
                  <b>{def.tgName ?? def.name ?? t('s3.botName')}</b>
                  <i>{def.botWho ?? t('s3.botSub')}</i>
                </span>
                {/* Значок Телеграма справа — как значок Инстаграма у инстаграм-сцен (владелец 24.10.2026). */}
                <TgGlyph className="onb-hdr-tg" size={20} />
              </>
            )}
          </div>
          <div className="onb-ph-body">
            {atStart ? (
              <div className={`onb-tgstart${def.tgAbout && !def.tgAbout.h && !def.tgAbout.p ? ' nocard' : ''}`}>
                <span className="onb-tgs-ava" aria-hidden>
                  <Pava src={def.tgAva ?? SCENE_BOT_AVA} size={64} />
                </span>
                <b>{def.tgName ?? def.name ?? t('s3.botName')}</b>
                {/* §10.5: у ботов сцен цепочки (g2, s5) описания нет — пустые заголовок и текст, карточки нет. */}
                {def.tgAbout && !def.tgAbout.h && !def.tgAbout.p ? null : (
                  <div className="onb-tgs-card">
                    <b>{def.tgAbout?.h ?? t('s3.aboutH')}</b>
                    <p>{def.tgAbout?.p ?? t('s3.about')}</p>
                  </div>
                )}
                <KBareButton
                  className={`onb-tgs-go${startLive ? ' live' : ''}${startLive && wig ? ' wig' : ''}`}
                  onClick={tapChip}
                >
                  {def.tgAbout?.btn ?? t('s3.startBtn')}
                </KBareButton>
              </div>
            ) : (
              <>
                {feed}
                {composer}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="onb-bare">{feed}</div>
      )}
      {/* Низ телефона растворяется в фоне страницы: красивый обрез вместо жёсткой линии. */}
      {def.phone ? <span className="onb-fade" aria-hidden /> : null}
      {/* Отдельная стрелка ВВЕРХ у самого самолёта (владелец 27.08: «чтобы точно
          поняли, что нажать») — прыгает под правым краем композера. */}
      {def.own && wait === 'send' ? (
        <span className="onb-send-arrow" aria-hidden>
          <ArrowUp size={18} strokeWidth={2.6} />
        </span>
      ) : null}
      {/* Что сейчас надо сделать — всегда написано под телефоном (владелец 27.08);
          дёргается вместе с живым местом, телефон в этот момент вибрирует. */}
      {/* Подсказка ЕДИНА на всех сценах (владелец 28.08: «Нажмите Начать» была жирной
          и без перелива): нежирная, с шайном, на одной высоте. */}
      <div className={`onb-hint shine${wait ? ' on' : ''}${wait && wig && !def.own ? ' wig' : ''}`}>
        <span>{hintNow}</span>
      </div>
      {finished && (!still || showReplay) ? (
        <div className="onb-endrow">
          {/* «Заново» — голая надпись со значком, без фона и радиуса (владелец 28.08). */}
          <KBareButton className="onb-again" onClick={replay}>
            <RotateCcw size={16} strokeWidth={2.2} />
            {t('hint.again')}
          </KBareButton>
          {/* На последнем слайде по окончании — ТОЛЬКО «Заново» по центру (владелец
              28.08); «Зарегистрироваться» живёт внизу шторки вместо «Пропустить».
              В статичном режиме второй кнопки нет вовсе (ТЗ-9 Е1). */}
          {still || isLast ? null : onNext ? (
            <KBareButton className="onb-next" onClick={onNext}>
              {t('hint.next')}
              <ArrowRight size={16} strokeWidth={2.4} />
            </KBareButton>
          ) : null}
        </div>
      ) : null}
      <div className="onb-cel">
        {plates.map((p) => (
          <div className="onb-cel-i" key={p.id} style={{ '--i': p.slot } as CSSProperties}>
            <span className="onb-conf" aria-hidden>
              {p.bits.map((b) => (
                <i
                  key={b.i}
                  className={`onb-bit c${b.c}${b.bar ? ' bar' : ''}${p.big ? ' xl' : ''}`}
                  style={
                    {
                      '--x1': `${b.x1}px`,
                      '--y1': `${b.y1}px`,
                      '--x': `${b.x}px`,
                      '--y': `${b.y}px`,
                      '--r': `${b.r}deg`,
                      animationDelay: `${b.d}s`,
                    } as CSSProperties
                  }
                />
              ))}
            </span>
            <span className={p.big ? 'onb-plate big' : 'onb-plate'}>
              <span className="onb-plate-c">
                <Check size={p.big ? 20 : 15} strokeWidth={3} />
              </span>
              {p.big && p.text.includes(' — ') ? (
                <span className="onb-plate-2">
                  <small>{p.text.split(' — ')[0]}</small>
                  <b>{p.text.split(' — ').slice(1).join(' — ')}</b>
                </span>
              ) : (
                p.text
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** ВСЕ сцены примеров; порядок массива = порядок тура (бэклог «Примеры работ 31.08»:
 *  слово в комментарии → подарок ИГ → подарок ТГ → цепочка рассылок), спрятанные — в
 *  хвосте. Реплики s1–s3 СНЯТЫ с наших шаблонов bot_templates (приказ владельца 28.08:
 *  диалоги не сочиняются) — меняется шаблон, правим и ключи auth.onboarding. Тексты
 *  подарка ТГ (s4) — по мотивам шаблона «Проверка подписки», цепочки (s5) — черновик
 *  исполнителя 01.09 (шаблона-источника у цепочки пока нет), оба на приёмке владельца. */
export function useScenes(): SceneDef[] {
  const t = useTranslations('auth.onboarding');
  // Определения живут в lib/scene-script.ts (одна истина с приложением, ТЗ NATIVE-SCENES §3.2 п.1) — здесь обёртка.
  const locale = useLocale();
  // §8.22: язык — для набора серии сцен с цепочкой (g2, s5).
  return useMemo<SceneDef[]>(() => sceneDefs(t as unknown as SceneT, locale === 'en' ? 'en' : 'ru'), [t, locale]);
}

/** Сцены тура и лендинга — без спрятанных. Анкеты и /wizard-scene ищут сцену по ключу
 *  в ПОЛНОМ списке useScenes — состав тура на их адреса не влияет. */
/**
 * СЦЕНЫ ТУРА И ЛЕНДИНГОВ — ПОД НИШУ ЧЕЛОВЕКА (портрет клиента, бэклог 31.08).
 * Ниша берётся из local storage (разлогинился — мы её помним), а если её там нет —
 * каждый заход показываем новую по кругу. Справочник сюжетных слов приезжает открытой
 * ручкой; не приехал — сцены остаются такими, какие есть, и ничего не ломается.
 * Своё лицо и свою публикацию тут не подставляем: снаружи человека мы не знаем.
 */
export function useTourScenes(): SceneDef[] {
  const all = useScenes();
  const locale = useLocale();
  const visible = useMemo(() => all.filter((s) => !s.hidden), [all]);
  const niche = useVisitNiche();
  const t = useTranslations('auth.onboarding.pers');
  return useMemo(() => {
    if (!niche) return visible;
    // Подстановку делает словарь (ICU-переменная {promise}) — из кода в строку мы не
    // лезем: текст фразы правится переводом, а не сборкой.
    // На лендинге лица клиента нет (снаружи мы его не знаем), поэтому пол неизвестен —
    // публичный ответ идёт нейтральной формой (pub: gender 'other'). Слова, обещание и
    // подарок — по нише захода (владелец 03.09.2026).
    // Слова собирает общий хелпер (одна точка истины на три места: тур, мастер в вебе,
    // страница сцены для приложения). Ключ сцены отдаём ему: у «доводит до оплаты» (s3)
    // свой сюжет, и фразы там другие.
    return visible.map((d) =>
      isWizardScene(d.k) ? nicheScene(d, nichePhrases(t, niche, null, d.k), niche.key, null, locale) : d,
    );
  }, [visible, niche, t, locale]);
}

/**
 * Сцены ЛЕНДИНГА: каждая сцена мастера — со СВОЕЙ случайной нишей, без повторов (владелец
 * 14.10.2026: «много примеров с разными нишами — рандомно в каждом виджете», а не одна ниша
 * захода во всех). Своя ниша из local storage тут НЕ берётся и счётчик ротации не крутится —
 * тур кабинета и приложения (useTourScenes) живёт по-прежнему, это отдельная рука.
 */
/** Сцены лендинга с закреплённой нишей: у «Слова в комментарии» (s2) в посте фото с фитнесом,
 *  поэтому сюжет всегда «Спорт и фитнес» (владелец 14.10.2026). Остальные — жребием. */
const LANDING_FIXED_NICHE: Partial<Record<string, string>> = { s2: 'sport' };

export function useLandingScenes(): SceneDef[] {
  const all = useScenes();
  const locale = useLocale();
  const visible = useMemo(() => all.filter((s) => !s.hidden), [all]);
  const niches = useNicheList();
  const t = useTranslations('auth.onboarding.pers');
  const [order, setOrder] = useState<string[] | null>(null);
  // Жребий — один раз за жизнь страницы и только в эффекте: Math.random в отрисовке дал бы
  // расхождение серверной разметки с клиентской и менял бы примеры под пальцем.
  useEffect(() => {
    if (niches.length === 0) return;
    setOrder((cur) => {
      if (cur) return cur;
      const keys = niches.map((n) => n.key);
      for (let i = keys.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [keys[i], keys[j]] = [keys[j]!, keys[i]!];
      }
      return keys;
    });
  }, [niches]);
  return useMemo(() => {
    if (!order) return visible;
    const rest = order.filter((k) => !Object.values(LANDING_FIXED_NICHE).includes(k));
    let n = 0;
    return visible.map((d) => {
      if (!isWizardScene(d.k)) return d;
      const key = LANDING_FIXED_NICHE[d.k] ?? rest[n++ % rest.length];
      const niche = niches.find((x) => x.key === key);
      return niche ? nicheScene(d, nichePhrases(t, niche, null, d.k), niche.key, null, locale) : d;
    });
  }, [visible, order, niches, t, locale]);
}

/** Список ниш с сервера (тот же ключ кэша, что у useVisitNiche). */
function useNicheList(): NicheView[] {
  const locale = useLocale();
  const { data } = usePersistentData<{ ok: boolean; niches: NicheView[] }>(
    `niches:${locale}`,
    `/api/niches?locale=${locale}`,
    { accept: (j) => Array.isArray((j as { niches?: unknown } | null)?.niches) },
  );
  return useMemo(() => data?.niches ?? [], [data]);
}

/** Ниша этого захода: своя из local storage либо очередная по кругу. */
function useVisitNiche(): NicheView | null {
  const locale = useLocale();
  const { data } = usePersistentData<{ ok: boolean; niches: NicheView[] }>(
    `niches:${locale}`,
    `/api/niches?locale=${locale}`,
    { accept: (j) => Array.isArray((j as { niches?: unknown } | null)?.niches) },
  );
  const niches = useMemo(() => data?.niches ?? [], [data]);
  const [key, setKey] = useState<string | null>(null);
  // Ниша выбирается ОДИН раз за жизнь страницы и ТОЛЬКО в эффекте: pickNicheForVisit
  // читает и пишет local storage, а на сервере его нет — выбор прямо в отрисовке дал бы
  // расхождение серверной разметки с клиентской (и крутил бы счётчик ротации на каждом
  // обновлении данных, меняя примеры прямо под пальцем).
  useEffect(() => {
    if (niches.length === 0) return;
    setKey((cur) => cur ?? pickNicheForVisit(niches.map((n) => n.key)));
  }, [niches]);
  return niches.find((n) => n.key === key) ?? null;
}

/** Тема, ВЫБРАННАЯ человеком (запись localStorage 'scriptera-theme'), либо null, если
 *  выбора не было. Нативному приветствию (helloAuthState) отдаём только её: атрибут
 *  data-theme без выбора равен системной, и натив принял бы её за выбор. */
function storedTheme(): 'light' | 'dark' | null {
  try {
    const t = localStorage.getItem('scriptera-theme');
    return t === 'dark' || t === 'light' ? t : null;
  } catch {
    return null;
  }
}

/** Веб живёт внутри нативной шторки: класс ставит и снимает натив (НВ8а п.2). Здесь он
 *  выключает веб-приветствие — выбор человек уже сделал на нативном герое. */
function useInSheet(): boolean {
  const [v, setV] = useState(false);
  useEffect(() => {
    const el = document.documentElement;
    const read = () => setV(el.classList.contains('in-sheet'));
    read();
    const mo = new MutationObserver(read);
    mo.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => mo.disconnect();
  }, []);
  return v;
}

export function AppOnboarding({
  backdrop = false,
  quiet = false,
}: {
  /** ПОДЛОЖКА ЛИСТА ВХОДА В БРАУЗЕРЕ (ТЗ WEB-LOOK Э1.4, владелец 30.09.2026: «мобильный
   *  /login — как в iOS, приветствие с шарами и шторка сверху»). Кнопки приветствия
   *  интро НЕ прячут, а просят хост (auth/auth-host.tsx) открыть лист: «Войти» — вход,
   *  «Зарегистрироваться» — регистрацию; мини-тур как был, по его концу — лист
   *  регистрации, а под ним снова приветствие. Только браузер: в приложении хоста нет. */
  backdrop?: boolean;
  /** Подложка без кнопок (прямой заход на /verify-phone и /welcome): те же шары и знак. */
  quiet?: boolean;
} = {}) {
  const t = useTranslations('auth.onboarding');
  const scenes = useTourScenes();
  // ЧИСЛО СЛАЙДОВ — ЧИСТО ВЕБ (владелец 28.08): натив о нём не знает, слайды и их
  // кнопки редактируются деплоем веба без пересборки и модерации приложения.
  const LAST = scenes.length - 1;
  const [show, setShow] = useState(false);
  // Тур В БРАУЗЕРЕ — карточкой-листом над приветствием (кадр iOS A02-tour-1: в приложении
  // тур стоит в родном листе над нативным героем). Решается после монтирования по UA, чтобы
  // разметка сервера и клиента совпадала; в приложении разметка прежняя (display: contents).
  const [browser, setBrowser] = useState(false);
  useEffect(() => setBrowser(!isNativeApp()), []);
  // Приветствие (владелец 26.08): знак в центре как на заставке, заголовок лендинга,
  // значки каналов и «Посмотреть примеры»; оттуда знак перелетает в угол к примерам.
  const [hello, setHello] = useState(true);
  // Подъём связки после появления: знак стартует ровно в точке заставки (центр −40) и
  // через мгновение пружинно отъезжает выше — как в нативном приветствии.
  const [risen, setRisen] = useState(false);
  const [step, setStep] = useState(0);
  // ТУР С ПЕРВОГО КАДРА (владелец 23.10.2026: «регистрация на долю секунды, затем тур»): закладка `scrHelloGo`
  // (выбор «Мини-тур»/«Зарегистрироваться» с нативного героя, положенная страницей входа или ожидания) читается
  // ДО первой отрисовки — layout-эффектом, а не обычным эффектом после кадра. Основной эффект ниже снимает
  // закладку и применяет выбор как прежде; здесь только состояние, чтобы форма не мелькнула.
  useLayoutEffect(() => {
    if (!isNativeApp() || !nativeMethod('helloState')) return;
    let g: string | null = null;
    try {
      g = sessionStorage.getItem('scrHelloGo');
    } catch {
      g = null;
    }
    if (g === 'examples') {
      setHello(false);
      setShow(true);
      setStep(0);
    } else if (g === 'skip') {
      setShow(false);
    }
  }, []);
  /* «Зарегистрироваться» внизу — с ПЕРВОГО мгновения на последнем слайде (владелец
     28.08): решает живая позиция прокрутки, не осевший step (тот ждёт settle 120мс,
     и «Пропустить» успевала мелькнуть). */
  const [regNow, setRegNow] = useState(false);
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const inSheet = useInSheet();
  // РЕМЕНЬ С ДВУХ СТОРОН (владелец 28.08: «после каждой выкатки появляется верхнее
  // меню»): класс in-sheet ставит натив, но перезагрузка страницы (деплой, смена
  // локали) его стирала — в приложении с нативным приветствием веб-интро живёт
  // ТОЛЬКО в шторке, поэтому веб ставит класс сам и хром не возвращается никогда.
  useEffect(() => {
    if (isNativeApp() && nativeMethod('helloState')) {
      document.documentElement.classList.add('in-sheet');
    }
  }, []);
  // В шторке приветствия нет вовсе: там живой нативный герой, а веб сразу открывает
  // страницы примеров.
  const onHello = hello && !inSheet;
  useEffect(() => {
    if (!show || !onHello) return;
    const id = setTimeout(() => setRisen(true), 350);
    return () => clearTimeout(id);
  }, [show, onHello]);
  const touchX = useRef<number | null>(null);
  const deckRef = useRef<HTMLDivElement | null>(null);
  const settleT = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Прокрутку двигает пипса — короткая глухота слушателя, чтобы не драться со снэпом. */
  const deaf = useRef(false);

  // СВАЙП — РОДНАЯ ПРОКРУТКА WebKit (скролл-снэп): та же физика UIScrollView, что у
  // нативных страниц, без единого прохода через мост — прокачка позиции жестом через
  // evaluateJavaScript дёргала кадры (владелец 27.08). Метод моста tourState остался
  // в сборках, но веб его больше не зовёт — нативный пан молчит (count=0 по умолчанию).

  useEffect(() => {
    // Браузер: интро рисуется только подложкой листа входа (Э1.4); прежняя страница
    // регистрации в браузере интро не показывала — так и осталось.
    if (!isNativeApp()) {
      if (backdrop) setShow(true);
      return;
    }
    // Мы на экране входа — значит, в приложение не вошли: натив запомнит и на следующем
    // холодном старте покажет НАТИВНОЕ приветствие (старые сборки метода не имеют).
    // Тема — только явный выбор человека (localStorage 'scriptera-theme'); нет записи —
    // поле не шлём, и натив идёт за системой (см. login-form.tsx, «тема честная»).
    const chosen = storedTheme();
    nativeBridge()
      ?.helloAuthState?.({ in: false, ...(chosen ? { theme: chosen } : {}), lang: locale })
      ?.catch(() => {});
    // Приветствие рисует НАТИВ? Тогда веб своё не показывает: ждём выбор человека —
    // «примеры» открывает экраны примеров сразу в углу (без веб-перелёта, знак уже
    // прилетел нативно), «пропустить» оставляет чистый вход. Выбор, сделанный до
    // монтирования, приходит в ответе helloState (take), позже — событием helloChoice.
    if (nativeMethod('helloState')) {
      const bridge = nativeBridge();
      let dead = false;
      let poll: ReturnType<typeof setInterval> | null = null;
      const apply = (choice: string) => {
        if (dead || !choice) return;
        if (poll) {
          clearInterval(poll);
          poll = null;
        }
        if (choice === 'examples') {
          // Тур — заново и с первой страницы: выбор мог прийти ПОВТОРНО после
          // «Войти»/«Зарегистрироваться» (владелец 27.08 — контент шторки обязан
          // меняться под каждую кнопку).
          setHello(false);
          setShow(true);
          setStep(0);
          setTimeout(() => deckRef.current?.scrollTo({ left: 0 }), 60);
        } else if (choice === 'login') {
          // «Войти» с нативного приветствия: уводим со страницы регистрации на вход.
          router.replace('/login');
        } else if (choice === 'skip') {
          // «Зарегистрироваться»: интро прячется — под ним чистая регистрация.
          setShow(false);
        }
      };
      // Закладка со страницы входа: выбор пришёл, пока в шторке был вход, — страница
      // регистрации исполняет его при загрузке.
      try {
        const g = sessionStorage.getItem('scrHelloGo');
        if (g) {
          sessionStorage.removeItem('scrHelloGo');
          apply(g);
        }
      } catch {
        /* нет sessionStorage — штатный путь опроса */
      }
      // Слушатели — ВСЕГДА и на всю жизнь страницы (владелец 28.08: «закрываешь и
      // открываешь шторку — кнопки прекращают работать»). Шторка открывается много
      // раз БЕЗ перезагрузки веба: если выбор съеден ответом helloState при загрузке,
      // а слушатель не повешен, все следующие нажатия героя уходят в пустоту.
      bridge?.addListener?.('helloChoice', (d) => apply(typeof d.key === 'string' ? d.key : ''));
      // Щёлкалка языка на нативном приветствии: запоминаем выбор и перегружаем
      // страницу входа под оверлеем на нужную локаль.
      bridge?.addListener?.('helloLang', (d) => {
        const lang = d.key === 'en' ? 'en' : 'ru';
        if (lang === locale) return;
        rememberLangCookie(lang);
        router.replace(pathname, { locale: lang });
      });
      // Тема с нативного приветствия: атрибут + память на месте, без перезагрузки.
      bridge?.addListener?.('helloTheme', (d) => {
        applyTheme(d.key === 'dark' ? 'dark' : 'light');
      });
      // Страница входа — заранее в кэш роутера: «Войти» обязан менять экран сразу,
      // без видимой паузы «открылась регистрация, потом заменилась» (владелец 28.08).
      router.prefetch('/login');
      bridge
        ?.helloState?.()
        .then((st) => {
          if (dead) return;
          if (st.choice) apply(st.choice);
          else if (st.active) {
            // Второй ремень: событие могло потеряться (страница перегружалась в момент
            // выбора) — выбор лежит в helloState (take) и добирается опросом.
            poll = setInterval(() => {
              bridge
                ?.helloState?.()
                .then((s) => {
                  if (s.choice) apply(s.choice);
                  else if (!s.active && poll) {
                    clearInterval(poll);
                    poll = null;
                  }
                })
                .catch(() => {});
            }, 700);
          } else {
            // Натив приветствие не рисует (например, разлогин посреди работы) —
            // показываем веб-вариант.
            setShow(true);
          }
        })
        .catch(() => {
          if (!dead) setShow(true);
        });
      return () => {
        dead = true;
        if (poll) clearInterval(poll);
      };
    }
    setShow(true);
  }, []);
  if (!show) return null;

  // «Мини-тур»: лента примеров; на подложке лист входа (если открыт) уходит.
  const startTour = () => {
    if (backdrop) openAuth('tour');
    setHello(false);
  };

  // «Пропустить мини-тур» и свайп за последнюю страницу: интро закрывается, под ним —
  // регистрация (веб сам её и рисует, в шторке тоже).
  const done = () => {
    // Подложка листа входа (Э1.4): интро остаётся приветствием, поверх — лист регистрации.
    if (backdrop) {
      setHello(true);
      openAuth('register');
      return;
    }
    // В приложении с методом authOpen выход из тура («Зарегистрироваться» и
    // «Пропустить») открывает ГОТОВУЮ нативную форму регистрации (владелец 28.08);
    // старые сборки и веб — как раньше, своя форма под туром.
    if (isNativeApp() && nativeMethod('authOpen')) {
      nativeBridge()?.authOpen?.({ mode: 'register' });
      return;
    }
    setShow(false);
  };

  return (
    <div
      className={`onb${onHello && risen ? ' risen' : ''}${hello ? '' : ' in-tour'}`}
      data-mode={onHello ? 'hello' : 'demo'}
      onTouchStart={(e) => {
        touchX.current = e.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e) => {
        const x0 = touchX.current;
        touchX.current = null;
        const x1 = e.changedTouches[0]?.clientX;
        if (x0 == null || x1 == null) return;
        const dx = x1 - x0;
        if (Math.abs(dx) <= 40) return;
        // Свайп на приветствии открывает примеры; страницы листает родной скролл сам.
        // Тихая подложка (телефон/аккаунт) примеров не открывает.
        if (onHello && dx < 0 && !quiet) startTour();
      }}
    >
      {/* Кнопки как на лендинге (владелец 26.08): язык и тема — те же «щёлкалки». */}
      <div className="onb-ctl2">
        <LocaleSwitch compact className="onb-tctl" />
        <ThemeToggle compact className="onb-tctl" />
      </div>

      {/* ЖИВОЙ ЗНАК — один элемент на оба режима, перелетает CSS-переходом.
          Режим hello: центр, точка и размер 1:1 с нативной заставкой (BrandSplashView:
          центр Y −40, знак 96, подпись через 14 — склейка без дёргания). Режим demo:
          верхний левый угол, стопка листов растворяется по пути, имя подъезжает
          справа. Числа держит onboarding.css (.onb-mark/.onb-mark-nm). */}
      <div className="onb-mark">
        <span className="onb-mark-lg">
          <BrandBolt size={96} />
        </span>
      </div>
      <span className="onb-mark-nm">Scriptera</span>

      {onHello ? (
        <>
          {/* Кружки-«пузыри» лендинга фоном (мини-герой, владелец 26.08). */}
          <div className="onb-orbs" aria-hidden>
            <span className="onb-orb pink" />
            <span className="onb-orb light" />
            <span className="onb-orb dark" />
            <span className="onb-orb light2" />
          </div>
          {/* «Войти» — пилюля слева сверху, как на лендинге. На подложке листа входа —
              та же пилюля, но открывает лист (Э1.4); тихая подложка её не рисует. */}
          {quiet ? null : backdrop ? (
            <KBareButton className="onb-login" onClick={() => openAuth('login')}>
              {t('login')}
            </KBareButton>
          ) : (
            <Link className="onb-login" href="/login">
              {t('login')}
            </Link>
          )}
          <div className="onb-hello">
            <h1 className="onb-h1">
              {t('h1')}
              {/* Значки каналов — ровно те же силуэты в фирменных цветах, что на лендинге;
                  стоят СРАЗУ ПОСЛЕ последнего слова заголовка, как в iOS (кадр A01-welcome). */}
              <span className="onb-chan" aria-hidden>
                <svg className="ig" viewBox="0 0 24 24" width="26" height="26">
                  <path d="M7.4 2.6h9.2a4.8 4.8 0 0 1 4.8 4.8v9.2a4.8 4.8 0 0 1-4.8 4.8H7.4a4.8 4.8 0 0 1-4.8-4.8V7.4a4.8 4.8 0 0 1 4.8-4.8zm4.6 4.6a4.8 4.8 0 1 0 0 9.6 4.8 4.8 0 0 0 0-9.6zm0 2.2a2.6 2.6 0 1 1 0 5.2 2.6 2.6 0 0 1 0-5.2zm5.3-3.1a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4z" />
                </svg>
                <svg className="tg" viewBox="0 0 24 24" width="26" height="26">
                  <path d="M21.6 3.4 2.9 10.6c-1 .4-1 1.2 0 1.5l4.7 1.5 1.8 5.5c.2.7.6.8 1.2.3l2.6-2.2 4.7 3.5c.9.5 1.5.2 1.7-.8l3-14c.3-1.2-.4-1.8-1-1.5zM8.8 13.2l9-5.6c.4-.2.8-.1.5.2l-7.5 6.8-.3 3.1-1.7-4.5z" />
                </svg>
              </span>
            </h1>
            <p className="onb-sub2">{t('sub')}</p>
          </div>
          {/* Кнопки — внизу, «под большой палец» (владелец 26.08), отдельно от связки. */}
          {quiet ? null : (
            <div className="onb-hello-cta">
              <KBareButton className="onb-go" onClick={startTour}>
                {t('examples')}
              </KBareButton>
              {/* «Зарегистрироваться»: интро закрывается — под ним регистрация (на
                  подложке листа — открывается лист регистрации). */}
              <KBareButton className="onb-skip" onClick={done}>
                {t('signup')}
              </KBareButton>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Подложка листа тура в браузере (.onb-deckbg; .onb-back занят стрелкой сцены): приветствие (фон и кружки) колодой за карточкой,
              как родной герой за родным листом. */}
          {browser ? (
            <div className="onb-deckbg" aria-hidden>
              <span className="onb-orb pink" />
              <span className="onb-orb light" />
              <span className="onb-orb dark" />
              <span className="onb-orb light2" />
            </div>
          ) : null}
          {/* Карточка-лист (в приложении и на широком экране — display: contents). */}
          <div className="onb-card">
            {browser ? <span className="onb-grab" aria-hidden /> : null}
            {/* Лента страниц — родной горизонтальный скролл со снэпом (физика WebKit,
              как UIScrollView). Сцена живёт только на открытой странице, сюжет
              играется с начала. Узкий призрак в хвосте: заехал на него — конец тура. */}
            <div
              className="onb-deck"
              ref={deckRef}
              onScroll={() => {
                const el = deckRef.current;
                if (!el) return;
                const w = el.clientWidth || 1;
                // Живой флаг последнего слайда — сразу по позиции, даже во время
                // программной прокрутки (deaf) и до оседания.
                setRegNow(el.scrollLeft > (LAST - 0.5) * w);
                if (deaf.current) return;
                // За последнюю страницу заехали на треть призрака — тур закончен: тот же
                // выход, что «Пропустить»/«Зарегистрироваться» (в приложении с authOpen —
                // ГОТОВАЯ нативная форма, иначе своя регистрация под туром).
                if (el.scrollLeft > (LAST + 0.35) * w) {
                  done();
                  return;
                }
                if (settleT.current) clearTimeout(settleT.current);
                settleT.current = setTimeout(() => {
                  const i = Math.min(LAST, Math.round((el.scrollLeft || 0) / w));
                  setStep(i);
                }, 120);
              }}
            >
              {scenes.map((d, i) => (
                <div className="onb-page" key={i}>
                  <div className="onb-pg">
                    {/* Заголовок 24/700 слева и ОДНО описание, как всё работает — больше
                      ничего (владелец 27.08: «заголовок и описание, больше ничего»). */}
                    <h1 className="onb-title">{t(`${d.k}.title` as 's1.title')}</h1>
                    <p className="onb-sub">{t(`${d.k}.text` as 's1.text')}</p>
                  </div>
                  <Scene
                    def={d}
                    on
                    active={step === i}
                    isLast={i === LAST}
                    onNext={() => {
                      const el = deckRef.current;
                      const to = Math.min(LAST, i + 1);
                      setStep(to);
                      if (!el) return;
                      deaf.current = true;
                      setTimeout(() => {
                        deaf.current = false;
                      }, 450);
                      el.scrollTo({
                        left: to * el.clientWidth,
                        behavior: reducedMotion() ? 'auto' : 'smooth',
                      });
                    }}
                  />
                </div>
              ))}
              <div className="onb-page ghost" aria-hidden />
            </div>

            <div className="onb-ctl">
              <div className="onb-pips">
                {scenes.map((_, i) => (
                  <KBareButton
                    key={i}
                    aria-label={`${i + 1}/${scenes.length}`}
                    onClick={() => {
                      const el = deckRef.current;
                      setStep(i);
                      setRegNow(i === LAST);
                      if (!el) return;
                      deaf.current = true;
                      setTimeout(() => {
                        deaf.current = false;
                      }, 450);
                      el.scrollTo({
                        left: i * el.clientWidth,
                        behavior: reducedMotion() ? 'auto' : 'smooth',
                      });
                    }}
                    className={`onb-pip${i === step ? ' on' : ''}`}
                  />
                ))}
              </div>
              {/* Слот ФИКСИРОВАННОЙ высоты: «Пропустить» и капсула живут в одном месте,
                пипсы над ними не сдвигаются ни на пиксель (владелец 28.08). */}
              <div className="onb-foot">
                {regNow ? (
                  <KBareButton className="onb-next" onClick={done}>
                    {t('hint.reg')}
                    <ArrowRight size={16} strokeWidth={2.4} />
                  </KBareButton>
                ) : (
                  <KBareButton className="onb-skip" onClick={done}>
                    {t('skiptour')}
                  </KBareButton>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
